<script setup lang="ts">
  import { BOTTOM_BAR_HEIGHT_VAR } from '@tundr/ds-foundations';
  import { Button, ButtonVariant, PageFooter } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { useResizeObserver } from '@vueuse/core';
  import { computed, onUnmounted, ref } from 'vue';
  import { useWizardStore } from '../../store/wizard-stepper.store';
  import type {
    WizardFooterFlag,
    WizardStepGuard,
  } from '../WizardSidebar/WizardSidebar.types';
  import type {
    WizardFooterActionPayload,
    WizardFooterProps,
  } from './WizardFooter.types';

  const props = defineProps<WizardFooterProps>();

  const emit = defineEmits<{
    next: [payload: WizardFooterActionPayload];
    back: [payload: WizardFooterActionPayload];
    submit: [payload: WizardFooterActionPayload];
    close: [payload: WizardFooterActionPayload];
  }>();

  const { t } = useLocale('wizard-stepper');

  const stepperStore = useWizardStore();

  const footerRef = ref<InstanceType<typeof PageFooter> | null>(null);

  const publishHeight = (height: number) => {
    document.documentElement.style.setProperty(
      BOTTOM_BAR_HEIGHT_VAR,
      `${height}px`,
    );
  };

  useResizeObserver(footerRef, ([entry]) => {
    publishHeight(entry.target.getBoundingClientRect().height);
  });

  onUnmounted(() => {
    document.documentElement.style.removeProperty(BOTTOM_BAR_HEIGHT_VAR);
  });

  const stepper = computed(() => stepperStore.steppers[props.namespace]);

  const activeIndex = computed(() => stepper.value?.activeIndex ?? 0);

  const totalSteps = computed(() => stepper.value?.steps.length ?? 0);

  const currentStep = computed(() => stepper.value?.steps[activeIndex.value]);

  const footerConfig = computed(() => currentStep.value?.footer);

  const footerStartComponent = computed(
    () => footerConfig.value?.startComponent,
  );

  const footerStartProps = computed(() => footerConfig.value?.startProps ?? {});

  const resolveFlag = (
    flag: WizardFooterFlag | undefined,
  ): boolean | undefined => (typeof flag === 'function' ? flag() : flag);

  const isRecapStep = computed(() => currentStep.value?.isRecap ?? false);

  const isFirstStep = computed(() => activeIndex.value === 0);

  const isLastStep = computed(
    () => totalSteps.value > 0 && activeIndex.value === totalSteps.value - 1,
  );

  const hideBack = computed(
    () => resolveFlag(footerConfig.value?.hideBack) ?? isRecapStep.value,
  );

  const showBack = computed(() => !isFirstStep.value && !hideBack.value);

  const showPrimary = computed(
    () => !resolveFlag(footerConfig.value?.hideNext),
  );

  const isPrimaryLoading = computed(
    () => footerConfig.value?.nextLoading?.() === true,
  );

  const isPrimaryDisabled = computed(
    () =>
      footerConfig.value?.nextDisabled?.() === true || isPrimaryLoading.value,
  );

  const primaryDisabledReason = computed(() => {
    if (footerConfig.value?.nextDisabled?.() !== true) {
      return undefined;
    }
    const reason = footerConfig.value?.nextDisabledReason;
    return typeof reason === 'function' ? reason() : reason;
  });

  const backLabel = computed(
    () => footerConfig.value?.backLabel ?? props.backLabel ?? t('footer.back'),
  );

  const primaryLabel = computed(() => {
    if (isRecapStep.value) {
      return (
        footerConfig.value?.submitLabel ??
        props.submitLabel ??
        t('footer.close')
      );
    }
    if (isLastStep.value) {
      return (
        footerConfig.value?.submitLabel ??
        props.submitLabel ??
        t('footer.submit')
      );
    }
    return footerConfig.value?.nextLabel ?? props.nextLabel ?? t('footer.next');
  });

  const buildPayload = (step: NonNullable<typeof currentStep.value>) => ({
    activeIndex: activeIndex.value,
    step,
  });

  const runGuard = async (guard?: WizardStepGuard) => {
    if (!guard) {
      return true;
    }
    return (await guard()) !== false;
  };

  const onBackClick = () => {
    if (!currentStep.value) {
      return;
    }
    emit('back', buildPayload(currentStep.value));
    stepperStore.previousStep(props.namespace);
  };

  const onPrimaryClick = async () => {
    const step = currentStep.value;
    if (!step) {
      return;
    }

    if (isRecapStep.value) {
      if (!(await runGuard(step.footer?.onSubmit))) {
        return;
      }
      emit('close', buildPayload(step));
      return;
    }

    if (isLastStep.value) {
      if (!(await runGuard(step.footer?.onSubmit))) {
        return;
      }
      emit('submit', buildPayload(step));
      return;
    }

    if (!(await runGuard(step.footer?.onNext))) {
      return;
    }
    emit('next', buildPayload(step));
    stepperStore.nextStep(props.namespace);
  };
</script>

<template>
  <PageFooter v-if="stepper" ref="footerRef" fixed :test-id="testId">
    <template v-if="footerStartComponent || $slots['footer-start']" #default>
      <component
        :is="footerStartComponent"
        v-if="footerStartComponent"
        v-bind="footerStartProps"
      />
      <slot
        v-else
        name="footer-start"
        :active-index="activeIndex"
        :step="currentStep"
      />
    </template>
    <template #actions>
      <Button
        v-if="showBack"
        :variant="ButtonVariant.Text"
        :test-id="testId ? `${testId}-back` : undefined"
        @click="onBackClick"
      >
        {{ backLabel }}
      </Button>
      <Button
        v-if="showPrimary"
        :variant="ButtonVariant.Primary"
        :disabled="isPrimaryDisabled"
        :disabled-reason="primaryDisabledReason"
        :test-id="testId ? `${testId}-primary` : undefined"
        @click="onPrimaryClick"
      >
        {{ primaryLabel }}
      </Button>
    </template>
  </PageFooter>
</template>
